/**
 * Newsletter Operations Card — modular editorial control panel for the
 * bi-weekly public newsletter pipeline.
 *
 * Top to bottom:
 *   [Postmark health indicator]
 *   [Subscriber tiles · 4 columns]
 *   [Flash banner]
 *   [Queued draft banner — if any]
 *   [Modular Editorial Editor — 5 inputs]
 *     • Hero Banner URL
 *     • Hero CTA Headline + Link  (2 inline inputs)
 *     • Founder Avatar URL
 *     • Founder Quick Caption
 *     • Main Editorial Message    (textarea, markdown-light)
 *   [Action ladder: TEST · Preview · Send to All Confirmed]
 *   [Schedule for next slot CTA]
 *   [Recent issues]  [Pending double-opt-in queue]  [Recent confirmed signups]
 *
 * Brand colours match hibff.com:
 *   Purple #7B4FD8 · Orange #F5831F · Ink #1A1209
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Send, Loader2, CheckCircle, Eye, Mail, Newspaper,
  Users as UsersIcon, Clock, ShieldCheck, CalendarClock,
  X as XIcon, Image as ImageIcon, Link as LinkIcon, MessageSquare,
  CircleUser, Activity, Plus, Trash2, Type, Palette,
  GripVertical, ArrowUp, ArrowDown,
} from 'lucide-react';
import { apiClient } from '../../api/client';
import NewsletterImageDropzone from './NewsletterImageDropzone';

const BRAND_PURPLE = '#7B4FD8';
const BRAND_ORANGE = '#F5831F';
const BRAND_INK = '#1A1209';

// Official Founder portrait — Robert Parson, Founder & CEO HiBFF.
// Stored permanently in the Emergent customer-assets bucket.
const DEFAULT_AVATAR = 'https://customer-assets.emergentagent.com/job_d90a863a-276f-4111-948b-fe6dddce366d/artifacts/35mmaidy_Me%202a.png';

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
    }) + ' UTC';
  } catch { return iso.slice(0, 16); }
};

const Field = ({ label, hint, children, testid }) => (
  <div className="mb-3" data-testid={testid}>
    <label className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#666' }}>
      <span>{label}</span>
      {hint && <span className="text-gray-400 normal-case font-normal tracking-normal text-[10px]">{hint}</span>}
    </label>
    {children}
  </div>
);

const HealthDot = ({ health }) => {
  // health: {status, sender, detail, server_name}
  const isOk = health?.status === 'ok';
  const isWarn = health && ['missing_sender', 'missing_token'].includes(health.status);
  const color = isOk ? '#10B981' : isWarn ? '#F59E0B' : health ? '#DC2626' : '#9CA3AF';
  const label = !health ? 'checking…' :
    isOk ? `Postmark · live (${health.server_name || 'connected'})` :
    `Postmark · ${health.status.replace(/_/g, ' ')}`;
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-md mb-3"
      style={{ backgroundColor: isOk ? '#ECFDF5' : isWarn ? '#FFFBEB' : health ? '#FEE2E2' : '#F3F4F6',
               border: `1px solid ${color}33` }}
      data-testid="newsletter-postmark-health"
      title={health?.detail || 'Probing Postmark token + sender…'}
    >
      <span
        className="inline-block rounded-full"
        style={{
          width: 10, height: 10, backgroundColor: color,
          boxShadow: isOk ? `0 0 0 3px ${color}33` : 'none',
          animation: isOk ? 'none' : (!health ? 'pulse 2s ease-in-out infinite' : 'none'),
        }}
      />
      <span className="text-[11px] font-semibold" style={{ color: isOk ? '#065F46' : isWarn ? '#92400E' : health ? '#991B1B' : '#6B7280' }}>
        {label}
      </span>
      {health?.sender && (
        <span className="text-[10px] ml-auto font-mono" style={{ color: '#6B7280' }}>
          from: {health.sender}
        </span>
      )}
    </div>
  );
};

const NewsletterOperationsCard = () => {
  const [stats, setStats] = useState(null);
  const [issues, setIssues] = useState([]);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [health, setHealth] = useState(null);

  // Modular editorial state — all 5 fields the new template understands.
  const [heroBannerUrl, setHeroBannerUrl] = useState('');
  const [heroCtaHeadline, setHeroCtaHeadline] = useState('');
  const [heroCtaLink, setHeroCtaLink] = useState('');
  const [founderAvatarUrl, setFounderAvatarUrl] = useState(DEFAULT_AVATAR);
  const [founderCaption, setFounderCaption] = useState('Robert · Founder, HiBFF');
  const [mainMessage, setMainMessage] = useState('');

  // Curated posts (max 4) + typography controls.
  const [posts, setPosts] = useState([
    { image_url: '', title: '', body: '' },
  ]);
  const [fontSize, setFontSize] = useState('16px');
  const [fontFamily, setFontFamily] = useState('Georgia');
  const [textColor, setTextColor] = useState('#1A1209');

  const updatePost = (idx, key, value) => {
    setPosts((prev) => prev.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));
  };
  const addPost = () => {
    if (posts.length >= 4) return;
    setPosts((prev) => [...prev, { image_url: '', title: '', body: '' }]);
  };
  const removePost = (idx) => {
    setPosts((prev) => prev.filter((_, i) => i !== idx));
  };
  // Feb 2026 — reorder curated posts via drag-and-drop OR the ↑↓
  // buttons (kept for accessibility + touch devices where HTML5 DnD
  // is inconsistent). Both funnel through this single helper so the
  // reorder logic stays canonical.
  const movePost = (from, to) => {
    if (from === to || from < 0 || to < 0) return;
    setPosts((prev) => {
      if (to >= prev.length) return prev;
      const next = prev.slice();
      const [row] = next.splice(from, 1);
      next.splice(to, 0, row);
      return next;
    });
  };
  // Drag-state — only the row whose handle is being held is `draggable`,
  // so cursor-drag inside inputs/textareas doesn't accidentally initiate
  // a card drag. `dragOverIdx` powers the drop-target highlight.
  const [dragEnabledIdx, setDragEnabledIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // ── Feb 2026 — Autosave working draft ────────────────────────────────
  // Persists the entire composition (hero, founder, curated posts +
  // order, typography) to `newsletter_autosave` on the backend so the
  // operator can close the tab mid-edit and pick up exactly where they
  // left off. `hydratedRef` blocks the initial state → save race that
  // would otherwise overwrite the saved draft with the empty defaults
  // on first paint.
  const [autosaveStatus, setAutosaveStatus] = useState('idle'); // idle | saving | saved | error
  const [autosaveAt, setAutosaveAt] = useState(null);
  const hydratedRef = React.useRef(false);
  const saveTimerRef = React.useRef(null);

  const buildPayload = useCallback(() => ({
    main_message: mainMessage.trim() || null,
    hero_banner_url: heroBannerUrl.trim() || null,
    hero_cta_headline: heroCtaHeadline.trim() || null,
    hero_cta_link: heroCtaLink.trim() || null,
    founder_avatar_url: founderAvatarUrl.trim() || null,
    founder_caption: founderCaption.trim() || null,
    posts: posts.filter((p) => p.title?.trim() || p.body?.trim() || p.image_url?.trim()),
    font_size: fontSize,
    font_family: fontFamily,
    text_color: textColor,
  }), [mainMessage, heroBannerUrl, heroCtaHeadline, heroCtaLink, founderAvatarUrl, founderCaption, posts, fontSize, fontFamily, textColor]);

  const hasEditorialContent = !!(mainMessage.trim() || heroBannerUrl.trim() || heroCtaHeadline.trim());

  const load = async () => {
    try {
      const [s, i, h] = await Promise.all([
        apiClient.request('/api/admin/newsletter/subscribers'),
        apiClient.request('/api/admin/newsletter/issues'),
        apiClient.request('/api/admin/newsletter/postmark-health').catch(() => null),
      ]);
      setStats(s);
      setIssues(i.issues || []);
      if (h) setHealth(h);
    } catch (e) {
      if (!(e.message || '').includes('403')) console.warn('newsletter load', e);
    }
  };
  useEffect(() => { load(); }, []);

  // ── Autosave — hydrate on mount, then debounce-save on every change ──
  // Load once on mount, populate all state, then flip `hydratedRef` so
  // the debounced save-effect below starts firing. Race-safe: if the
  // component is torn down mid-fetch we skip the setState calls.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.request('/api/admin/newsletter/autosave');
        if (cancelled) return;
        const d = res?.draft;
        if (d) {
          if (typeof d.founder_note === 'string') setMainMessage((prev) => prev || d.founder_note);
          if (typeof d.main_message === 'string') setMainMessage(d.main_message);
          if (typeof d.hero_banner_url === 'string') setHeroBannerUrl(d.hero_banner_url);
          if (typeof d.hero_cta_headline === 'string') setHeroCtaHeadline(d.hero_cta_headline);
          if (typeof d.hero_cta_link === 'string') setHeroCtaLink(d.hero_cta_link);
          if (typeof d.founder_avatar_url === 'string' && d.founder_avatar_url) setFounderAvatarUrl(d.founder_avatar_url);
          if (typeof d.founder_caption === 'string') setFounderCaption(d.founder_caption);
          if (Array.isArray(d.posts) && d.posts.length) setPosts(d.posts);
          if (typeof d.font_size === 'string') setFontSize(d.font_size);
          if (typeof d.font_family === 'string') setFontFamily(d.font_family);
          if (typeof d.text_color === 'string') setTextColor(d.text_color);
        }
        if (res?.saved_at) setAutosaveAt(res.saved_at);
      } catch (e) {
        if (!(e.message || '').includes('403')) console.warn('autosave hydrate', e);
      } finally {
        if (!cancelled) hydratedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced save on any editable-state change (skip until hydrated).
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setAutosaveStatus('saving');
      try {
        const res = await apiClient.request('/api/admin/newsletter/autosave', {
          method: 'PUT',
          body: JSON.stringify(buildPayload()),
        });
        setAutosaveAt(res?.saved_at || new Date().toISOString());
        setAutosaveStatus('saved');
      } catch (e) {
        setAutosaveStatus('error');
        console.warn('autosave save', e);
      }
    }, 700);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
    // buildPayload captures every editable piece of state, so this deps
    // list is sufficient — any content change bumps its identity.
  }, [buildPayload]);

  const clearAutosave = async () => {
    if (!window.confirm('Discard the saved draft? All hero, founder and post content will be cleared.')) return;
    try {
      await apiClient.request('/api/admin/newsletter/autosave', { method: 'DELETE' });
    } catch (e) { console.warn('autosave clear', e); }
    // Also reset the form to defaults so the operator sees a fresh slate.
    setHeroBannerUrl(''); setHeroCtaHeadline(''); setHeroCtaLink('');
    setFounderAvatarUrl(DEFAULT_AVATAR); setFounderCaption('Robert · Founder, HiBFF');
    setMainMessage('');
    setPosts([{ image_url: '', title: '', body: '' }]);
    setFontSize('16px'); setFontFamily('Georgia'); setTextColor('#1A1209');
    setAutosaveAt(null); setAutosaveStatus('idle');
  };

  // Feb 2026 — cross-component "Load queued draft into editor" bridge.
  // The Queue Stack tile dispatches a window CustomEvent when the
  // operator clicks "Load" on a queued slot; we hydrate the form here.
  useEffect(() => {
    const onLoad = (e) => {
      const d = e?.detail;
      if (!d || typeof d !== 'object') return;
      if (typeof d.main_message === 'string') setMainMessage(d.main_message);
      if (typeof d.hero_banner_url === 'string') setHeroBannerUrl(d.hero_banner_url);
      if (typeof d.hero_cta_headline === 'string') setHeroCtaHeadline(d.hero_cta_headline);
      if (typeof d.hero_cta_link === 'string') setHeroCtaLink(d.hero_cta_link);
      if (typeof d.founder_avatar_url === 'string' && d.founder_avatar_url) setFounderAvatarUrl(d.founder_avatar_url);
      if (typeof d.founder_caption === 'string') setFounderCaption(d.founder_caption);
      if (Array.isArray(d.posts) && d.posts.length) setPosts(d.posts);
      if (typeof d.font_size === 'string') setFontSize(d.font_size);
      if (typeof d.font_family === 'string') setFontFamily(d.font_family);
      if (typeof d.text_color === 'string') setTextColor(d.text_color);
    };
    window.addEventListener('hibff:newsletter:load-into-editor', onLoad);
    return () => window.removeEventListener('hibff:newsletter:load-into-editor', onLoad);
  }, []);

  const sendTest = async () => {
    const override = window.prompt(
      'Send a TEST newsletter to which email?\n(Leave blank to send to your own admin email.)',
      ''
    );
    if (override === null) return;
    setBusy('test'); setMsg(null); setErr(null);
    try {
      const qs = override.trim() ? `?to=${encodeURIComponent(override.trim())}` : '';
      const res = await apiClient.request(`/api/admin/newsletter/test${qs}`, {
        method: 'POST', body: JSON.stringify(buildPayload()),
      });
      if (res.success) {
        const pmOk = res.postmark?.success;
        setMsg(pmOk ? `Test delivered to ${res.to}.` : `Test attempted to ${res.to} — Postmark reported: ${res.postmark?.error || 'no confirmation'}`);
      } else setErr(res.error || 'Test send failed');
    } catch (e) { setErr(e.message || 'Test send failed'); }
    setBusy('');
  };

  const broadcast = async () => {
    if (!stats?.confirmed) {
      setErr('No confirmed subscribers yet — nothing to send.');
      return;
    }
    if (!window.confirm(
      `Broadcast the current issue to ${stats.confirmed} confirmed subscriber(s)?\n\n` +
      `Hero banner: ${heroBannerUrl ? 'YES' : 'no'}\n` +
      `CTA: ${heroCtaHeadline ? '"' + heroCtaHeadline + '"' : 'no'}\n` +
      `Founder section: ${(mainMessage || founderCaption) ? 'YES' : 'no'}\n\n` +
      'This is idempotent — each subscriber only receives the issue once per slot.'
    )) return;
    setBusy('send'); setMsg(null); setErr(null);
    try {
      const res = await apiClient.request('/api/admin/newsletter/send', {
        method: 'POST', body: JSON.stringify(buildPayload()),
      });
      if (res.skipped) setMsg(`Slot ${res.slot} already sent (issue ${res.issue_id}).`);
      else {
        setMsg(`Broadcast: ${res.sent}/${res.recipients} delivered · slot ${res.slot}`);
        // Feb 2026 — a successful broadcast retires the working draft
        // so the next composition starts on a clean slate. Fire-and-forget;
        // a stale draft is a minor annoyance, not a correctness issue.
        try {
          await apiClient.request('/api/admin/newsletter/autosave', { method: 'DELETE' });
          setAutosaveAt(null);
          setAutosaveStatus('idle');
        } catch (e) { /* ignore */ }
      }
      load();
    } catch (e) { setErr(e.message || 'Broadcast failed'); }
    setBusy('');
  };

  const forceConfirm = async (email) => {
    if (!window.confirm(`Force-confirm "${email}"?\n\nBypasses double-opt-in, audit-logged.`)) return;
    setBusy(`fc:${email}`); setMsg(null); setErr(null);
    try {
      const res = await apiClient.request('/api/admin/newsletter/force-confirm', {
        method: 'POST', body: JSON.stringify({ email }),
      });
      if (res.already_confirmed) setMsg(`${email} was already confirmed.`);
      else if (res.success) setMsg(`Force-confirmed ${email}.`);
      else setErr(res.detail || 'Force-confirm failed');
      load();
    } catch (e) { setErr(e.message || 'Force-confirm failed'); }
    setBusy('');
  };

  const scheduleDraft = async () => {
    // Sprint 189: hand the FULL curated payload to the Tuesday cron — not
    // just the main message. The backend persists every field (hero, CTA,
    // founder, posts, typography) so the automated send doesn't degrade.
    const payload = buildPayload();
    const hasContent = !!(
      payload.main_message || payload.hero_banner_url ||
      payload.hero_cta_headline || payload.founder_caption ||
      (payload.posts && payload.posts.length)
    );
    if (!hasContent) {
      setErr('Add a main message, hero banner, CTA, founder caption, or at least one post before scheduling.');
      return;
    }
    if (stats?.queued_draft && !window.confirm(`Overwrite the existing queued draft for slot ${stats.queued_draft.slot}?`)) return;
    setBusy('schedule'); setMsg(null); setErr(null);
    try {
      const res = await apiClient.request('/api/admin/newsletter/drafts', {
        method: 'POST', body: JSON.stringify(payload),
      });
      if (res.success) {
        const postCount = res.post_count || 0;
        setMsg(`Scheduled for slot ${res.slot} · ${res.fields_persisted?.length || 0} fields, ${postCount} post(s) queued.`);
        setMainMessage('');
        setPosts([{ image_url: '', title: '', body: '' }]);
      } else {
        setErr(res.detail || 'Schedule failed');
      }
      load();
    } catch (e) { setErr(e.message || 'Schedule failed'); }
    setBusy('');
  };

  const cancelDraft = async () => {
    if (!window.confirm('Cancel the queued draft for the next slot?')) return;
    setBusy('cancel-draft'); setMsg(null); setErr(null);
    try {
      const res = await apiClient.request('/api/admin/newsletter/drafts', { method: 'DELETE' });
      if (res.success) setMsg(`Queued draft cleared for slot ${res.slot}.`);
      load();
    } catch (e) { setErr(e.message || 'Cancel failed'); }
    setBusy('');
  };

  if (!stats) return null;

  // Build the preview URL with every modular field URL-encoded so the
  // opened tab renders EXACTLY what Postmark will send.
  const previewHref = (() => {
    const params = new URLSearchParams();
    const p = buildPayload();
    Object.entries(p).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      if (k === 'posts') {
        if (Array.isArray(v) && v.length) params.set('posts', JSON.stringify(v));
      } else if (v) {
        params.set(k, v);
      }
    });
    const qs = params.toString();
    return `${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/preview${qs ? '?' + qs : ''}`;
  })();

  const tilesData = [
    { label: 'Confirmed', value: stats.confirmed, color: '#10B981' },
    { label: 'Pending opt-in', value: stats.pending, color: BRAND_ORANGE },
    { label: 'Unsubscribed', value: stats.unsubscribed, color: '#9CA3AF' },
    { label: 'Next slot', value: stats.next_slot || '—', color: BRAND_PURPLE },
  ];

  const inputStyle = {
    border: '1px solid #E0E0E0', backgroundColor: '#FAFAFA',
    color: BRAND_INK, fontFamily: 'Helvetica, Arial, sans-serif',
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="newsletter-operations">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#EDE9FE' }}>
          <Newspaper size={18} style={{ color: BRAND_PURPLE }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold" style={{ color: BRAND_INK }}>Public Newsletter · Editorial Studio</h3>
          <p className="text-xs" style={{ color: '#666' }}>
            Modular hero · founder profile · editorial body · bi-weekly Tuesday 10:00 UTC.
          </p>
        </div>
      </div>

      {/* Postmark live health probe */}
      <HealthDot health={health} />

      {/* Subscriber tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="newsletter-stats-grid">
        {tilesData.map((t) => (
          <div key={t.label} className="text-center rounded-lg py-2 px-2" style={{ backgroundColor: '#FAFAFA', border: '1px solid #EEE' }}>
            <div className="text-base font-black" style={{ color: t.color }}>{t.value}</div>
            <div className="text-[9px] text-gray-500 uppercase tracking-wider">{t.label}</div>
          </div>
        ))}
      </div>

      {/* Flash banner */}
      {(msg || err) && (
        <div className="text-xs mb-3 px-3 py-2 rounded-md"
             style={{ backgroundColor: err ? '#FEE2E2' : '#D1FAE5', color: err ? '#991B1B' : '#065F46' }}
             data-testid="newsletter-flash">
          {err || msg}
        </div>
      )}

      {/* Queued draft banner */}
      {stats.queued_draft && (
        <div className="mb-3 px-3 py-2.5 rounded-md flex items-start gap-2"
             style={{ backgroundColor: '#FEF3C7', border: `1px solid ${BRAND_ORANGE}` }}
             data-testid="newsletter-queued-draft-banner">
          <CalendarClock size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#92400E' }} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: '#92400E' }}>
              Queued for slot {stats.queued_draft.slot} · auto-fires next Tuesday 10:00 UTC
            </p>
            <p className="text-xs italic" style={{ color: '#78350F', fontFamily: 'Georgia, serif', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              &ldquo;{stats.queued_draft.founder_note}&rdquo;
            </p>
            <p className="text-[10px] mt-0.5" style={{ color: '#92400E' }}>
              Queued by {stats.queued_draft.queued_by} · {fmt(stats.queued_draft.queued_at)}
            </p>
          </div>
          <button
            onClick={cancelDraft}
            disabled={busy === 'cancel-draft'}
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-md disabled:opacity-50 flex-shrink-0"
            style={{ backgroundColor: '#FFF', color: '#92400E', border: `1px solid ${BRAND_ORANGE}` }}
            data-testid="newsletter-cancel-draft-btn"
          >
            {busy === 'cancel-draft' ? <Loader2 size={10} className="animate-spin" /> : <XIcon size={10} />}
            Cancel
          </button>
        </div>
      )}

      {/* ─── MODULAR EDITORIAL EDITOR ──────────────────────────────── */}
      <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: '#FAFAFA', border: '1px dashed #E0E0E0' }} data-testid="newsletter-editor">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: BRAND_PURPLE }}>Editorial Studio</span>
          <span className="text-[10px]" style={{ color: '#6B6257' }}>Every field optional — empty sections collapse cleanly.</span>
          {/* Autosave status pill (Feb 2026). Live indicator + one-click
              clear. Only shown once the draft has been touched or a
              persisted draft was hydrated on mount. */}
          <div className="ml-auto flex items-center gap-2" data-testid="newsletter-autosave-status">
            {autosaveStatus === 'saving' && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3F0FB', color: BRAND_PURPLE }}>
                <Loader2 size={10} className="animate-spin" /> Saving draft…
              </span>
            )}
            {autosaveStatus === 'saved' && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#ECFDF5', color: '#065F46' }}>
                <CheckCircle size={10} /> Draft saved{autosaveAt ? ` · ${fmt(autosaveAt)}` : ''}
              </span>
            )}
            {autosaveStatus === 'error' && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                Autosave failed
              </span>
            )}
            {autosaveStatus === 'idle' && autosaveAt && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3F4F6', color: '#374151' }} title="Restored from server">
                <CheckCircle size={10} /> Draft restored · {fmt(autosaveAt)}
              </span>
            )}
            {autosaveAt && (
              <button
                type="button"
                onClick={clearAutosave}
                className="text-[10px] px-2 py-0.5 rounded-full hover:bg-red-50"
                style={{ color: '#991B1B', border: '1px solid #FCA5A5' }}
                data-testid="newsletter-autosave-clear"
                title="Discard the saved draft and start over"
              >
                Clear draft
              </button>
            )}
          </div>
        </div>

        {/* Section A — Hero Spotlight */}
        <Field
          label={<span className="inline-flex items-center gap-1"><ImageIcon size={11} /> Hero Banner</span>}
          hint="drag-and-drop or click to upload (JPG/PNG/WEBP ≤ 5 MB)"
          testid="newsletter-field-banner"
        >
          <NewsletterImageDropzone
            value={heroBannerUrl}
            onUploaded={setHeroBannerUrl}
            testIdBase="newsletter-hero-banner"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-3" data-testid="newsletter-field-cta">
          <div className="sm:col-span-2">
            <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1" style={{ color: '#666' }}>CTA Headline</label>
            <input
              type="text"
              value={heroCtaHeadline}
              onChange={(e) => setHeroCtaHeadline(e.target.value)}
              placeholder="Join the Schools Pilot"
              maxLength={120}
              className="w-full px-3 py-2 text-sm rounded-md"
              style={inputStyle}
              data-testid="newsletter-cta-headline-input"
            />
          </div>
          <div className="sm:col-span-3">
            <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1 inline-flex items-center gap-1" style={{ color: '#666' }}>
              <LinkIcon size={11} /> CTA Target Link
            </label>
            <input
              type="url"
              value={heroCtaLink}
              onChange={(e) => setHeroCtaLink(e.target.value)}
              placeholder="https://hibff.com/schools-pilot"
              className="w-full px-3 py-2 text-sm rounded-md"
              style={inputStyle}
              data-testid="newsletter-cta-link-input"
            />
          </div>
        </div>

        <div className="my-3" style={{ borderTop: '1px solid #ECECEC' }}></div>

        {/* Section B — Founder Editorial */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-3" data-testid="newsletter-field-founder">
          <div className="sm:col-span-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1 inline-flex items-center gap-1" style={{ color: '#666' }}>
              <CircleUser size={11} /> Founder Avatar
            </label>
            <NewsletterImageDropzone
              value={founderAvatarUrl}
              onUploaded={setFounderAvatarUrl}
              compact
              testIdBase="newsletter-avatar"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1" style={{ color: '#666' }}>Founder Quick Caption</label>
            <input
              type="text"
              value={founderCaption}
              onChange={(e) => setFounderCaption(e.target.value)}
              placeholder="Robert · Founder, HiBFF"
              maxLength={240}
              className="w-full px-3 py-2 text-sm rounded-md"
              style={{ ...inputStyle, fontStyle: 'italic' }}
              data-testid="newsletter-caption-input"
            />
          </div>
        </div>

        <Field
          label={<span className="inline-flex items-center gap-1"><MessageSquare size={11} /> Main Editorial Message</span>}
          hint={
            <span>
              Blank line for new paragraph · single newline = line break · <span className={mainMessage.length > 3600 ? 'text-red-500' : ''}>{mainMessage.length}/4000</span>
            </span>
          }
          testid="newsletter-field-message"
        >
          <textarea
            value={mainMessage}
            onChange={(e) => setMainMessage(e.target.value.slice(0, 4000))}
            rows={5}
            placeholder={'Friends — this fortnight we…\n\nSecond paragraph here.\n\n— Robert'}
            className="w-full px-3 py-2 text-sm rounded-md resize-y"
            style={{ ...inputStyle, fontFamily: 'Georgia, serif', fontStyle: 'italic', lineHeight: 1.55 }}
            data-testid="newsletter-main-message-input"
          />
        </Field>

        {/* ─── Typography Controls ───────────────────────────────────── */}
        <div className="my-3" style={{ borderTop: '1px solid #ECECEC' }}></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-3" data-testid="newsletter-typography-controls">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1 inline-flex items-center gap-1" style={{ color: '#666' }}>
              <Type size={11} /> Font Size
            </label>
            <select
              value={fontSize}
              onChange={(e) => setFontSize(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md"
              style={inputStyle}
              data-testid="newsletter-font-size-select"
            >
              <option value="14px">14px — Compact</option>
              <option value="16px">16px — Default</option>
              <option value="18px">18px — Large</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1 inline-flex items-center gap-1" style={{ color: '#666' }}>
              <Type size={11} /> Font Family
            </label>
            <select
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md"
              style={inputStyle}
              data-testid="newsletter-font-family-select"
            >
              <option value="Georgia">Georgia — Editorial Serif</option>
              <option value="Helvetica">Helvetica — Modern Sans</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1 inline-flex items-center gap-1" style={{ color: '#666' }}>
              <Palette size={11} /> Text Color
            </label>
            <select
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md"
              style={inputStyle}
              data-testid="newsletter-text-color-select"
            >
              <option value="#1A1209">Ink-Dark</option>
              <option value="#7B4FD8">Brand Purple</option>
              <option value="#475569">Slate Grey</option>
            </select>
          </div>
        </div>
      </div>

      {/* ─── CURATED POSTS MANAGER (max 4) ─────────────────────────── */}
      <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: '#FAFAFA', border: '1px dashed #E0E0E0' }} data-testid="newsletter-posts-manager">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: BRAND_PURPLE }}>Curated Posts</span>
            <span className="text-[10px]" style={{ color: '#6B6257' }}>Max 4 · Image (180×140 auto-cropped) · Title · Body · <b>Drag the ⋮⋮ handle or use ↑↓ to reorder</b></span>
          </div>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: posts.length >= 4 ? '#FEE2E2' : '#EDE9FE', color: posts.length >= 4 ? '#991B1B' : BRAND_PURPLE }}>
            {posts.length}/4
          </span>
        </div>
        <div className="space-y-3" data-testid="newsletter-posts-list">
          {posts.map((post, idx) => (
            <div
              key={idx}
              className="bg-white rounded-md p-3 transition-all"
              style={{
                border: dragOverIdx === idx && dragEnabledIdx !== null && dragEnabledIdx !== idx
                  ? `2px dashed ${BRAND_PURPLE}`
                  : '1px solid #E5E7EB',
                opacity: dragEnabledIdx === idx ? 0.55 : 1,
              }}
              draggable={dragEnabledIdx === idx}
              onDragStart={(e) => {
                if (dragEnabledIdx !== idx) { e.preventDefault(); return; }
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(idx));
              }}
              onDragEnd={() => {
                setDragEnabledIdx(null);
                setDragOverIdx(null);
              }}
              onDragOver={(e) => {
                // Only accept drops if a card drag is in progress.
                if (dragEnabledIdx === null) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverIdx !== idx) setDragOverIdx(idx);
              }}
              onDragLeave={() => {
                setDragOverIdx((v) => (v === idx ? null : v));
              }}
              onDrop={(e) => {
                e.preventDefault();
                const raw = e.dataTransfer.getData('text/plain');
                const from = parseInt(raw, 10);
                if (!Number.isNaN(from)) movePost(from, idx);
                setDragOverIdx(null);
                setDragEnabledIdx(null);
              }}
              data-testid={`newsletter-post-${idx}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {/* Feb 2026 — drag handle. Sets `draggable` on the card
                      only while grabbed, so text selection inside the
                      inputs/textareas below is not hijacked. */}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Drag to reorder post ${idx + 1}`}
                    onMouseDown={() => setDragEnabledIdx(idx)}
                    onMouseUp={() => { if (dragOverIdx === null) setDragEnabledIdx(null); }}
                    onTouchStart={() => setDragEnabledIdx(idx)}
                    onTouchEnd={() => setDragEnabledIdx(null)}
                    className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-gray-100 select-none"
                    style={{ color: '#9CA3AF' }}
                    data-testid={`newsletter-post-drag-handle-${idx}`}
                    title="Drag to reorder"
                  >
                    <GripVertical size={14} />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#374151' }}>Post {idx + 1}</span>
                </div>
                <div className="flex items-center gap-1">
                  {/* Accessible ↑↓ buttons (keyboard + touch parity with drag). */}
                  <button
                    type="button"
                    onClick={() => movePost(idx, idx - 1)}
                    disabled={idx === 0}
                    className="text-[11px] inline-flex items-center px-1.5 py-0.5 rounded disabled:opacity-30 hover:bg-gray-100"
                    style={{ color: '#374151' }}
                    data-testid={`newsletter-post-move-up-${idx}`}
                    title="Move up"
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => movePost(idx, idx + 1)}
                    disabled={idx === posts.length - 1}
                    className="text-[11px] inline-flex items-center px-1.5 py-0.5 rounded disabled:opacity-30 hover:bg-gray-100"
                    style={{ color: '#374151' }}
                    data-testid={`newsletter-post-move-down-${idx}`}
                    title="Move down"
                  >
                    <ArrowDown size={12} />
                  </button>
                  {posts.length > 1 && (
                    <button
                      onClick={() => removePost(idx)}
                      className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded ml-1"
                      style={{ color: '#991B1B', backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }}
                      data-testid={`newsletter-post-remove-${idx}`}
                      title="Remove this post"
                    >
                      <Trash2 size={11} /> Remove
                    </button>
                  )}
                </div>
              </div>
              <div className="mb-2">
                <NewsletterImageDropzone
                  value={post.image_url}
                  onUploaded={(url) => updatePost(idx, 'image_url', url)}
                  compact
                  testIdBase={`newsletter-post-image-${idx}`}
                />
              </div>
              <input
                type="text"
                placeholder="Post Title"
                value={post.title}
                onChange={(e) => updatePost(idx, 'title', e.target.value.slice(0, 160))}
                maxLength={160}
                className="w-full px-3 py-1.5 text-sm rounded-md mb-2 font-semibold"
                style={inputStyle}
                data-testid={`newsletter-post-title-${idx}`}
              />
              <textarea
                placeholder="Post Body Content (preserves line breaks)"
                value={post.body}
                onChange={(e) => updatePost(idx, 'body', e.target.value.slice(0, 1200))}
                rows={3}
                maxLength={1200}
                className="w-full px-3 py-2 text-sm rounded-md resize-y"
                style={inputStyle}
                data-testid={`newsletter-post-body-${idx}`}
              />
            </div>
          ))}
        </div>
        {posts.length < 4 && (
          <button
            onClick={addPost}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md transition-colors"
            style={{ backgroundColor: 'white', color: BRAND_PURPLE, border: `1px dashed ${BRAND_PURPLE}` }}
            data-testid="newsletter-post-add"
          >
            <Plus size={12} /> Add Post ({4 - posts.length} remaining)
          </button>
        )}
      </div>

      {/* Action ladder */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        <button
          onClick={sendTest}
          disabled={busy === 'test'}
          className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-lg disabled:opacity-50"
          style={{ backgroundColor: '#10B981', color: 'white' }}
          data-testid="newsletter-test-btn"
        >
          {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          Send TEST to Me
        </button>
        <a
          href={previewHref} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-lg"
          style={{ backgroundColor: BRAND_INK, color: 'white' }}
          data-testid="newsletter-preview-btn"
        >
          <Eye size={14} /> Preview Live Layout
        </a>
        <button
          onClick={broadcast}
          disabled={busy === 'send' || !stats.confirmed}
          className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
          style={{ backgroundColor: BRAND_ORANGE }}
          data-testid="newsletter-broadcast-btn"
          title={stats.confirmed ? `Broadcast to ${stats.confirmed} confirmed subscriber(s)` : 'No confirmed subscribers yet'}
        >
          {busy === 'send' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Send to All Confirmed
        </button>
      </div>

      {/* Schedule for next slot */}
      <div className="mb-4">
        <button
          onClick={scheduleDraft}
          disabled={busy === 'schedule' || !mainMessage.trim()}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors"
          style={{
            backgroundColor: mainMessage.trim() ? BRAND_PURPLE : '#F3F4F6',
            color: mainMessage.trim() ? 'white' : '#9CA3AF',
            border: mainMessage.trim() ? 'none' : '1px dashed #D1D5DB',
          }}
          data-testid="newsletter-schedule-btn"
          title={mainMessage.trim() ? `Queue for slot ${stats.next_unsent_slot}` : 'Type a Main Editorial Message above to enable scheduling'}
        >
          {busy === 'schedule' ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />}
          {stats.queued_draft
            ? `Replace queued draft for slot ${stats.next_unsent_slot}`
            : `Schedule for next slot (${stats.next_unsent_slot || stats.next_slot})`
          }
        </button>
        {!hasEditorialContent && !stats.queued_draft && (
          <p className="text-[10px] text-center mt-1.5" style={{ color: '#6B6257' }}>
            <Activity size={9} className="inline mr-1" />
            With no editorial content the email falls back to the auto-data feature spotlights + footnote metrics.
          </p>
        )}
      </div>

      {/* Recent issues */}
      {issues.length > 0 && (
        <div className="pt-3 border-t" style={{ borderColor: '#F1F1F1' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#6B6257' }}>Recent issues</p>
          <div className="space-y-1.5" data-testid="newsletter-issues-list">
            {issues.slice(0, 5).map((iss) => {
              const ok = (iss.sent_count || 0) > 0;
              const Icon = ok ? CheckCircle : Clock;
              return (
                <div key={iss.issue_id} className="flex items-center gap-2 px-3 py-2 rounded-md"
                     style={{ backgroundColor: '#FAFAFA', border: '1px solid #EEE' }}
                     data-testid={`newsletter-issue-${iss.issue_id}`}>
                  <Icon size={13} style={{ color: ok ? '#065F46' : '#92400E' }} />
                  <span className="text-xs font-mono" style={{ color: '#666' }}>{iss.cadence_slot}</span>
                  <span className="text-xs truncate" style={{ color: '#374151' }} title={iss.subject}>{iss.subject}</span>
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px]" style={{ color: '#065F46' }}>
                    <UsersIcon size={10} /> {iss.sent_count || 0} sent
                  </span>
                  <span className="text-[10px] text-gray-500">{fmt(iss.last_sent_at || iss.created_at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending double-opt-in queue */}
      {stats.pending_rows && stats.pending_rows.length > 0 && (
        <div className="pt-3 mt-3 border-t" style={{ borderColor: '#F1F1F1' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: '#92400E' }}>
            <Clock size={11} /> Pending double-opt-in · {stats.pending_rows.length}
          </p>
          <div className="space-y-1.5" data-testid="newsletter-pending-list">
            {stats.pending_rows.map((p) => {
              const isBusy = busy === `fc:${p.email}`;
              return (
                <div key={p.email} className="flex items-center gap-2 px-3 py-2 rounded-md"
                     style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}
                     data-testid={`newsletter-pending-${p.email}`}>
                  <Clock size={12} style={{ color: '#92400E' }} />
                  <span className="text-xs font-mono truncate" style={{ color: BRAND_INK }}>{p.email}</span>
                  <button
                    onClick={() => forceConfirm(p.email)}
                    disabled={isBusy}
                    className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md disabled:opacity-50"
                    style={{ backgroundColor: '#10B981', color: 'white' }}
                    data-testid={`newsletter-force-confirm-${p.email}`}
                  >
                    {isBusy ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />}
                    Force confirm
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterOperationsCard;
